import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MegaMenuItem } from 'primeng/api';
import { LayoutService } from '@/core/services/layout/layout.service';
import { SessionService } from '@/core/services/security/session.service';
import { FavoriteMenuService } from '@/core/services/security/favorite-menu.service';
import { UserProfileModalService } from '@/core/services/security/user-profile-modal.service';
import { ChangeTenantModalService } from '@/components/security/change-tenant-modal/services/change-tenant-modal.service';
import { NotificacaoMenuService } from '@/core/services/privado/notificacao/notificacao-menu.service';
import { NotificacaoModalService } from '@/components/security/notificacao/services/notificacao-modal.service';
import { MessagesUtils } from '@/core/utils/layout/messages.utils';
import { DateUtils } from '@/core/utils/date.utils';
import { MenuFavoritoModel } from '@/components/layout/menu/models/menu-favorito.model';
import { MenuSearchResultModel } from '@/components/layout/menu/models/menu-search-result.model';
import { NotificacaoDTO } from '@/core/domain/dto/privado/notificacao/notificacao.dto';
import { PermissaoTokenDTO } from '@/core/domain/dto/seguranca/acesso/oauth/permissao-token.dto';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GlobalEvents } from '@/events/global/global.events';

@Component({
    selector: '[app-topbar]',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    standalone: false,
    host: {
        class: 'layout-topbar'
    },
})
export class TopbarComponent implements OnInit, OnDestroy {
    layoutService = inject(LayoutService);
    sessionService = inject(SessionService);
    favoriteMenuService = inject(FavoriteMenuService);
    userProfileModalService = inject(UserProfileModalService);
    changeTenantModalService = inject(ChangeTenantModalService);
    notificacaoMenuService = inject(NotificacaoMenuService);
    notificacaoModalService = inject(NotificacaoModalService);
    messagesUtils = inject(MessagesUtils);
    dateUtils = inject(DateUtils);
    router = inject(Router);
    private globalEvents = inject(GlobalEvents);

    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild('searchPanel') searchPanel!: ElementRef<HTMLElement>;

    @ViewChild('menuButton') menuButton!: ElementRef<HTMLButtonElement>;

    @ViewChild('mobileMenuButton') mobileMenuButton!: ElementRef<HTMLButtonElement>;

    model: MegaMenuItem[] = [];
    searchTerm = '';
    searchResults: Array<MenuSearchResultModel> = [];
    notifications: Array<NotificacaoDTO> = [];
    unreadNotificationsCount = 0;
    canChangeTenant = false;
    private favoritosSubscription?: Subscription;
    private notificationsSubscription?: Subscription;
    private sessionRestoredSubscription?: Subscription;

    ngOnInit(): void {
        this.refreshCanChangeTenant();
        void this.notificacaoMenuService.refreshForLoggedUser(false);
        this.buildFavoritesMenu(this.favoriteMenuService.getFavoritos());
        this.favoritosSubscription = this.favoriteMenuService.favoritos$.subscribe((favoritos) => {
            this.buildFavoritesMenu(favoritos);
        });
        this.notifications = this.notificacaoMenuService.notificationsSnapshot;
        this.unreadNotificationsCount = this.notificacaoMenuService.unreadCount;
        this.notificationsSubscription = this.notificacaoMenuService.notifications$.subscribe((notifications) => {
            this.notifications = notifications;
            this.unreadNotificationsCount = this.notificacaoMenuService.unreadCount;
        });
        this.sessionRestoredSubscription = this.globalEvents.sessionRestored$.subscribe(() => {
            this.refreshCanChangeTenant();
            void this.notificacaoMenuService.refreshForLoggedUser(false);
        });
    }

    ngOnDestroy(): void {
        this.favoritosSubscription?.unsubscribe();
        this.notificationsSubscription?.unsubscribe();
        this.sessionRestoredSubscription?.unsubscribe();
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    onRightMenuButtonClick() {
        this.layoutService.openRightMenu();
    }

    toggleConfigSidebar() {
        let layoutState = { ... this.layoutService.layoutState() };

        if (this.layoutService.isSidebarActive()) {
            layoutState.overlayMenuActive = false;
            layoutState.overlaySubmenuActive = false;
            layoutState.staticMenuMobileActive = false;
            layoutState.menuHoverActive = false;
            layoutState.configSidebarVisible = false;
        }
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
        this.layoutService.layoutState.set(layoutState);
    }

    focusSearchInput() {
        setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.onSearchChange();
        }, 150);
    }

    onSearchChange(): void {
        const term = this.normalize(this.searchTerm);
        if (!term) {
            this.searchResults = [];
            return;
        }

        const permissoes = this.getPermissoes();
        const matched = new Map<string, MenuSearchResultModel>();

        for (const permissao of permissoes) {
            if (!this.matchesPermissao(permissao, term) || !permissao.funcionalidadeUrl) {
                continue;
            }

            const key = permissao.funcionalidadeUrl;
            if (matched.has(key)) {
                continue;
            }

            matched.set(key, {
                label: permissao.funcionalidadeNome || permissao.funcionalidadeRole,
                route: permissao.funcionalidadeUrl,
                icon: permissao.funcionalidadeIcone || 'pi pi-fw pi-search',
                modulo: permissao.funcionalidadeModuloPaiNome
                    || permissao.funcionalidadeModuloNome,
                role: permissao.funcionalidadeRole,
            });
        }

        this.searchResults = [...matched.values()].slice(0, 10);
    }

    selectSearchResult(result: MenuSearchResultModel, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        if (!result?.route) {
            return;
        }
        void this.router.navigateByUrl(result.route);
        this.closeSearchPanel();
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchResults = [];
    }

    closeSearchPanel(): void {
        this.clearSearch();
        this.searchPanel?.nativeElement?.classList?.add('!hidden');
    }

    onTopbarMenuToggle() {
        this.layoutService.layoutState.update((val) => ({ ...val, topbarMenuActive: !val.topbarMenuActive }));
    }

    openProfileOnAction(): void {
        void this.userProfileModalService.openLoggedUserProfile();
    }

    openChangeTenantOnAction(): void {
        void this.changeTenantModalService.openChangeTenant();
    }

    private refreshCanChangeTenant(): void {
        this.canChangeTenant = this.changeTenantModalService.canChangeTenant();
    }

    openNotificationOnAction(notificacao: NotificacaoDTO, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        void this.notificacaoModalService.openDetail(notificacao).then((updated) => {
            if (updated?.id) {
                this.notificacaoMenuService.upsertLocal(updated);
            }
        });
    }

    openAllNotificationsOnAction(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        void this.notificacaoMenuService.refreshForLoggedUser(false);
        void this.notificacaoModalService.openShowAll();
    }

    refreshNotificationsOnAction(): void {
        void this.notificacaoMenuService.refreshForLoggedUser(false);
    }

    getNotificationIcon(notificacao: NotificacaoDTO): string {
        return this.notificacaoMenuService.getDisplayIcon(
            notificacao,
            this.sessionService.getInstance()?.user?.username,
        );
    }

    formatNotificationDate(date?: string): string {
        return this.dateUtils.formatRelativeTime(date);
    }

    isNotificationUnread(notificacao: NotificacaoDTO): boolean {
        return this.notificacaoMenuService.isUnread(
            notificacao,
            this.sessionService.getInstance()?.user?.username,
        );
    }

    logoutOnAction() {
        this.sessionService.logout();
        this.messagesUtils.clear();
        setTimeout(() => {
            this.messagesUtils.showMessageErrorToast(
                "messages.error.userNotAuthenticated",
            );
        }, 300);
    }

    private getPermissoes(): Array<PermissaoTokenDTO> {
        return this.sessionService.getInstance()?.user?.perfis?.[0]?.permissoes || [];
    }

    private matchesPermissao(permissao: PermissaoTokenDTO, term: string): boolean {
        const fields = [
            permissao.funcionalidadeNome,
            permissao.funcionalidadeRole,
            permissao.funcionalidadeUrl,
            permissao.funcionalidadeModuloNome,
            permissao.funcionalidadeModuloPaiNome,
            permissao.funcionalidadeIcone,
        ];
        return fields.some((field) => this.normalize(field).includes(term));
    }

    private normalize(value?: string): string {
        return (value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    private buildFavoritesMenu(favoritos: Array<MenuFavoritoModel>): void {
        const items = favoritos.length
            ? favoritos.map((favorito) => ({
                label: favorito.label,
                icon: favorito.icon || 'pi pi-fw pi-star-fill',
                command: () => this.navigateToFavorite(favorito.route),
            }))
            : [{
                label: 'Nenhuma rotina favorita',
                icon: 'pi pi-fw pi-info-circle',
                disabled: true,
            }];

        this.model = [
            {
                label: 'Favoritos',
                items: [
                    [
                        {
                            label: 'Suas rotinas favoritas',
                            items,
                        }
                    ]
                ]
            }
        ];
    }

    private navigateToFavorite(route: string): void {
        if (!route) {
            return;
        }
        void this.router.navigateByUrl(route);
    }
}
